import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import {
  GridColDef,
  GridOverlay,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LiveDataRow } from "../models/LiveDataRow";
import { I18nGrid } from "@/components/data/I18nGrid";

interface Props {
  rows: LiveDataRow[];
  isLoading: boolean;
  isFetching: boolean;
  hasDevice: boolean;
}

function LiveDataToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter debounceMs={300} />
    </GridToolbarContainer>
  );
}

function NoDeviceOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography color="text.secondary">
        {t("liveData.noDeviceSelected", "Please select a device to view live data")}
      </Typography>
    </GridOverlay>
  );
}

function NoDataOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography color="text.secondary">
        {t("liveData.noData", "No data available for the selected device")}
      </Typography>
    </GridOverlay>
  );
}

export function LiveDataGrid({ rows, isLoading, isFetching, hasDevice }: Props) {
  const { t, i18n } = useTranslation();

  const columns: GridColDef<LiveDataRow>[] = useMemo(
    () => [
      {
        field: "dateTime",
        headerName: t("liveData.dateTime", "Timestamp"),
        flex: 1.5,
        minWidth: 180,
        type: "dateTime",
        valueGetter: (value: Date) => (value ? new Date(value) : null),
        valueFormatter: (value: Date | null) =>
          value
            ? value.toLocaleString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
      },
      {
        field: "diagnosticName",
        headerName: t("liveData.diagnosticName", "Diagnostic"),
        flex: 2,
        minWidth: 200,
      },
      {
        field: "value",
        headerName: t("liveData.value", "Value"),
        flex: 1,
        minWidth: 100,
        type: "number",
        align: "right",
        headerAlign: "right",
      },
      {
        field: "unitOfMeasure",
        headerName: t("liveData.unit", "Unit"),
        width: 100,
      },
{
        field: "source",
        headerName: t("liveData.source", "Source"),
        width: 120,
      },
      {
        field: "diagnosticId",
        headerName: t("liveData.diagnosticId", "Diagnostic ID"),
        flex: 1,
        minWidth: 150,
      },
    ],
    [t, i18n.language],
  );

  return (
    <Box sx={{ flex: 1, minHeight: 400, position: "relative" }}>
      {isFetching && !isLoading && (
        <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }} />
      )}
      <I18nGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        density="compact"
        initialState={{
          sorting: { sortModel: [{ field: "dateTime", sort: "desc" }] },
          columns: {
            columnVisibilityModel: {
              diagnosticId: false,
            },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        slots={{
          toolbar: LiveDataToolbar,
          noRowsOverlay: hasDevice ? NoDataOverlay : NoDeviceOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress" as const,
            noRowsVariant: "skeleton" as const,
          },
        }}
      />
    </Box>
  );
}
